import Icon from "~/components/ui/Icon"
import type { ComponentSignal } from "~/sdk/signal"
import type { ReactNode } from "react"
import { useLayoutEffect, useRef } from "react"

export interface Props {
    children: ReactNode
    length: number
    slide: ComponentSignal<number>
    animate: ComponentSignal<boolean>
}

type Transform = (currentSlide: number, length: number) => string | number | null | undefined

const Swiper = ({ children, length, slide, animate }: Props) => {
    const refSwiper = useRef<HTMLDivElement>(null)
    const refWrapper = useRef<HTMLDivElement>(null)

    useLayoutEffect(() => {
        const swiper = refSwiper.current
        const wrapper = refWrapper.current

        if (swiper === null || wrapper === null) return

        function transitionEnd() {
            if (slide.value === 1) {
                animate.value = false
                slide.value = length - 3
            }

            if (slide.value === length - 2) {
                animate.value = false
                slide.value = 2
            }
        }

        let startPoint = 0
        let savedPoint = 0
        let currentPoint = 0
        let movement = 0

        function touchMove(e: TouchEvent) {
            const point = e.touches[0].clientX
            movement = point - startPoint
            const _position = point - currentPoint

            // wrapper?.style.setProperty('transform', `translateX(${position}px)`)
            savedPoint = movement
        }

        function touchStart(e: TouchEvent) {
            startPoint = e.touches[0].clientX
            currentPoint = startPoint - savedPoint
            wrapper?.addEventListener('touchmove', touchMove)
        }

        function touchEnd() {
            wrapper?.removeEventListener('touchmove', touchMove)

            if (movement < -50) {
                slide.value = slide.value + 1
                animate.value = true
                return
            }

            if (movement > 50) {
                slide.value = slide.value - 1
                animate.value = true
                return
            }
        }

        wrapper.addEventListener('dragstart', (e) => e.preventDefault(), { passive: true })
        wrapper.addEventListener('transitionend', transitionEnd, { passive: true })
        wrapper.addEventListener('touchstart', touchStart, { passive: true })
        wrapper.addEventListener('touchend', touchEnd, { passive: true })

        return () => {
            wrapper.removeEventListener('transitionend', transitionEnd)
            wrapper.removeEventListener('touchmove', touchMove)
            wrapper.removeEventListener('touchstart', touchStart)
            wrapper.removeEventListener('touchend', touchEnd)
        }
    }, [])

    return (
        <div
            className='flex h-[9.7rem] items-center justify-center mx-auto max-w-[1730px] overflow-hidden py-10 px-4 relative w-full'
            ref={refSwiper}
        >
            <div className='absolute flex items-center justify-between max-w-[86.75rem] px-16 w-full lg:hidden'>
                <button
                    type='button'
                    className='bg-[#E1E0DC] flex items-center justify-center h-10 rounded-full w-10 z-50'
                    aria-label='Ver próximo item'
                    onClick={() => {
                        animate.value = true

                        if (slide.value <= 0) slide.value = length - 1
                        else slide.value -= 1
                    }}
                >
                    <Icon
                        className='h-[1.875rem] w-[1.875rem] object-contain text-black stroke-0'
                        size={24}
                        id='BannerChevronLeft'
                        strokeWidth={1}
                    />
                </button>

                <button
                    type='button'
                    className='bg-[#E1E0DC] flex items-center justify-center h-10 rounded-full w-10 z-50'
                    aria-label='Ver item anterior'
                    onClick={() => {
                        animate.value = true

                        if (slide.value <= length - 2) slide.value += 1
                        else slide.value = 0
                    }}
                >
                    <Icon
                        className='h-[1.875rem] w-[1.875rem] object-contain text-black stroke-0'
                        size={24}
                        id='BannerChevronRight'
                        strokeWidth={1}
                    />
                </button>
            </div>

            <div
                className='absolute duration-500 ease-in-out flex items-center gap-[0.625rem] left-1/2 transition-transform z-40'
                ref={refWrapper}
                style={{
                    transform: (transform(slide.value, length) ?? undefined) as string | undefined,
                    transition: animate.value ? undefined : 'none',
                }}
            >
                {children}
            </div>
        </div>
    )
}

function calcTranslate({ offset, slide }: { offset: number; slide: number }) {
    return offset / 2 + offset * slide
}

const transform: Transform = (slide, length) => {
    const offset = 100 / length
    const translate = calcTranslate({ offset, slide })

    // if(slide === 1) return `translateX(-${calcTranslate({offset, slide: length - 2})}%)`

    return `translateX(-${translate}%)`
}

export default Swiper
